import { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import {
  Button,
  Checkbox,
  Pagination,
  Spin,
  Row,
  Col,
  Table,
  Tooltip,
  Popover,
  Dropdown,
} from 'antd'
import {
  CloseOutlined,
  MenuOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import Style from './table.module.scss'
import usehook, { Props, checkbox } from './hook'
import { Resizable } from 'react-resizable'
import FilterList from './FilterList'

// 列头可拖动
const ResizeableTitle = (props: any) => {
  const { onResize, width, title, ...restProps } = props
  // 添加偏移量
  const [offset, setOffset] = useState<number>(0)
  if (!title && !onResize) {
    return <th {...restProps} />
  }
  return (
    <Resizable
      width={width + offset}
      height={0}
      // 拖拽事件实时更新
      onResize={(e, { size }) => {
        // 这里只更新偏移量，数据列表其实并没有伸缩
        setOffset(size.width - width)
      }}
      // 拖拽结束更新
      onResizeStop={(...argu) => {
        // 拖拽结束以后偏移量归零
        setOffset(0)
        // 这里是props传进来的事件，在外部是列数据中的onHeaderCell方法提供的事件，请自行研究官方提供的案例
        onResize(...argu)
      }}
      handle={
        <span
          // 有偏移量显示竖线
          className={`${Style['react-resizable-handle']} ${
            offset ? Style['active'] : ''
          }`}
          // 拖拽层偏移
          style={{ transform: `translateX(${offset}px)` }}
          onClick={(e) => {
            // 取消冒泡，不取消貌似容易触发排序事件
            e.stopPropagation()
            e.preventDefault()
          }}
        />
      }
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  )
}

//  自定义 table组件
const CustomTableGrid = (props: Props) => {
  const {
    dataSource,
    listColumn,
    headerTool,
    title,
    loading,
    onChangeCheck,
    Y,
    X,
    scrollCustom,
    scrollStyle,
    pagination,
    getRowStyle,
    ScreenColKey,
    ScreenColOnlyKey,
    selectRowType,
    paginationBtn,
    selfAdaptScroll,
    showSize,
    rowKey,
    defaultlist,
    onSelectRow,
    onSelectAll,
    filterType,
    positionFixed,
    filterSlotRef,
    ...restProps
  } = props
  const {
    state,
    onHandleChange,
    onHandleChange2,
    onKeyDown,
    handleVisibleChange,
    onSelectionChanged,
    onCellFocused,
    onClickClose,
    onClickSave,
    handleResize,
  } = usehook(props)
  const {
    filterbtn,
    checkBoxData,
    checkLength,
    filterCol,
    Column,
    focusData,
    highlight,
    checkedRows,
  } = state

  // Portal：挂载后检测外部插槽是否就绪，就绪后将按钮 portal 过去
  const [portalReady, setPortalReady] = useState(false)
  useEffect(() => {
    if (filterSlotRef?.current) {
      setPortalReady(true)
    }
  }, [filterSlotRef])
  const usePortal = filterType && portalReady && filterSlotRef?.current

  // 渲染 checkbox
  const renderCheckBox = (item: checkbox, index: number) => {
    return (
      <Checkbox
        key={item.key}
        onChange={() => onHandleChange(item, index)}
        checked={item.check}
        disabled={item.disabled}
      >
        <MenuOutlined
          style={{
            color: '#ddd',
            marginRight: '8px',
            verticalAlign: 'baseline',
            fontSize: '10px',
          }}
        />
        {item.name}
      </Checkbox>
    )
  }

  // 获取必选列key数组
  const getFixedItems = () => {
    if (!ScreenColOnlyKey) return undefined
    return Array.isArray(ScreenColOnlyKey)
      ? ScreenColOnlyKey
      : [ScreenColOnlyKey]
  }

  // 渲染列设置按钮（filterType模式使用Dropdown + FilterList）
  const renderColumnSetting = () => {
    if (filterType) {
      return (
        <Dropdown
          trigger={['click']}
          onOpenChange={handleVisibleChange}
          open={filterbtn}
          getPopupContainer={() => document.body}
          dropdownRender={() => (
            <div className="checkboxbox">
              <div className="checkboxheader">
                <span>可用列</span>
                <Button
                  className="icon"
                  onClick={onClickClose}
                  icon={<CloseOutlined />}
                />
              </div>
              <FilterList
                data={checkBoxData}
                fixedItems={getFixedItems()}
                onSave={(param) => onClickSave(param)}
                onHandleChange={onHandleChange2}
                positionFixed={positionFixed}
              />
            </div>
          )}
        >
          <Button
            type="link"
            size="small"
            className={Style.filterBtn}
            icon={<UnorderedListOutlined />}
          ></Button>
        </Dropdown>
      )
    }

    if (ScreenColKey) {
      return (
        <Popover
          trigger="click"
          placement="bottomRight"
          title="可配置字段"
          onOpenChange={handleVisibleChange}
          content={
            <div className="checkboxbox">
              <Row>
                {checkBoxData.map((item, index) => (
                  <Col key={item.key} span={24} className="item">
                    {renderCheckBox(item, index)}
                  </Col>
                ))}
              </Row>
            </div>
          }
        >
          <Button
            type="link"
            size="small"
            className={Style.filterBtn}
            icon={<UnorderedListOutlined />}
          ></Button>
        </Popover>
      )
    }

    return null
  }

  // 渲染列：filterType模式按Column（已排序）渲染，否则按listColumn原始顺序
  const renderColumns = () => {
    if (filterType) {
      return Column.map((item: any, index: number) =>
        filterCol.indexOf(item.key) !== -1 ? (
          <Table.Column
            {...item}
            key={`${index}_A`}
            ellipsis
            align="left"
            width={Column[index]?.width ?? 100}
            dataIndex={item.key}
            title={
              item.titleRender ? (
                item.titleRender
              ) : (
                <span title={item.title}>{item.title}</span>
              )
            }
            render={item.render}
            onHeaderCell={() => {
              return {
                className: Style['headercell'],
                width: Column[index]?.width ?? 100,
                onResize: handleResize(index),
              }
            }}
            onCell={(record: any, rowIndex) => {
              return {
                onClick: () =>
                  onCellFocused(
                    item.key ? record[String(item.key)] : null,
                    index,
                    rowIndex ? rowIndex : 0,
                  ),
                className: `${Style['cell']} ${
                  focusData.x === index && focusData.y === rowIndex
                    ? Style['focus']
                    : ''
                } ${
                  highlight.x === index && highlight.y === rowIndex
                    ? Style['copyHighlight']
                    : ''
                }`,
              }
            }}
          />
        ) : null,
      )
    }

    // 原有渲染逻辑
    return listColumn.map((item, index) =>
      filterCol.indexOf(item.key) !== -1 ? (
        <Table.Column
          {...item}
          key={`${index}_A`}
          ellipsis
          align="left"
          width={Column?.[index]?.width ?? 100}
          dataIndex={item.key}
          title={
            item.titleRender ? (
              item.titleRender
            ) : (
              <span title={item.title}>{item.title}</span>
            )
          }
          render={item.render}
          onHeaderCell={() => {
            return {
              className: Style['headercell'],
              width: Column?.[index]?.width ?? 100,
              onResize: handleResize(index),
            }
          }}
          onCell={(record, rowIndex) => {
            return {
              onClick: () =>
                onCellFocused(
                  item.key ? record[String(item.key)] : null,
                  index,
                  rowIndex ? rowIndex : 0,
                ),
              className: `${Style['cell']} ${
                focusData.x === index && focusData.y === rowIndex
                  ? Style['focus']
                  : ''
              } ${
                highlight.x === index && highlight.y === rowIndex
                  ? Style['copyHighlight']
                  : ''
              }`,
            }
          }}
        />
      ) : null,
    )
  }

  // 是否需要显示表头行（有标题、headerTool、或非portal的列设置按钮时才显示）
  const showHeaderRow =
    headerTool || title || (!usePortal && (filterType || ScreenColKey))

  return (
    <div
      className={`${Style.tablebox} ${
        selfAdaptScroll && Style['table-self-adapt']
      }`}
    >
      {/* 将列设置按钮 portal 到外部插槽 */}
      {usePortal &&
        ReactDOM.createPortal(renderColumnSetting(), filterSlotRef!.current!)}
      {showHeaderRow && (
        <div className={Style.headertitle}>
          {/* 左侧box */}
          <div className={Style.headertoolleft}>{headerTool}</div>
          {/* 标题 */}
          <div className={Style.headertext}>{title}</div>
          {/* 右侧筛选条件 */}
          <div className={Style.headertoolright}>
            {!usePortal && renderColumnSetting()}
          </div>
        </div>
      )}
      <Spin tip="数据加载中..." spinning={loading}>
        <div id="components-table-resizable-column">
          <Table
            rowKey={rowKey || 'id'}
            bordered
            scroll={
              selfAdaptScroll
                ? { x: X ? X : 800, y: 'calc(100% - 56px)' }
                : scrollCustom
                  ? scrollCustom
                  : { x: X ? X : 800, y: `calc(100vh - ${Y ? Y + 56 : 256}px)` }
            }
            style={
              selfAdaptScroll
                ? {
                    height: '100%',
                    borderColor: '#cccccc',
                  }
                : scrollStyle
                  ? scrollStyle
                  : {
                      height: `calc(100vh - ${Y ? Y : 200}px)`,
                      borderColor: '#cccccc',
                    }
            }
            getPopupContainer={(triggerNode) => document.body ?? triggerNode}
            pagination={false}
            dataSource={dataSource}
            components={{
              header: {
                cell: ResizeableTitle,
              },
            }}
            onRow={(record) => ({
              style: getRowStyle ? getRowStyle(record) : undefined,
              onCopy: onKeyDown,
            })}
            rowSelection={
              onChangeCheck
                ? {
                    selectedRowKeys: checkLength,
                    onChange: onSelectionChanged,
                    type: selectRowType ? selectRowType : 'checkbox',
                    columnWidth: '30px',
                    getCheckboxProps: (record) => ({
                      disabled: record.isDisabled || record.hideCheckbox,
                      className: record.hideCheckbox ? 'hidden' : '',
                    }),
                    onSelect: onSelectRow,
                    onSelectAll,
                  }
                : undefined
            }
            {...restProps}
          >
            {renderColumns()}
          </Table>
        </div>
        {pagination ? (
          <div className={Style.pagination}>
            <div className={Style.paginationleft}>
              {onChangeCheck ? (
                checkLength.length ? (
                  `已选择${defaultlist?.length ?? checkLength.length}项内容`
                ) : (
                  <span style={{ color: '#AAA' }}>
                    共{pagination.total}项内容
                  </span>
                )
              ) : null}
              {paginationBtn && (
                <>
                  {!checkLength.length && (
                    <span className={Style.paginationBtnTitle}>
                      <i />
                      <span>批量操作</span>
                    </span>
                  )}
                  <div className={Style.paginationBtnBox}>
                    {paginationBtn.map((item) => (
                      <Tooltip
                        key={item.name}
                        placement="bottomLeft"
                        title={`需勾选表格选项后进行批量${item.name}`}
                        overlayClassName={`${Style.paginationBtnToolTip} ${
                          checkLength.length
                            ? Style.paginationBtnToolTipHidden
                            : ''
                        }`}
                      >
                        <Button
                          onClick={() => item.event(checkLength, checkedRows)}
                          disabled={!checkLength.length}
                          style={{ marginRight: '8px' }}
                        >
                          {item.name}
                        </Button>
                      </Tooltip>
                    ))}
                  </div>
                </>
              )}
            </div>
            <Pagination
              showSizeChanger={showSize ? true : false}
              showQuickJumper
              pageSize={pagination?.pagesize || 25}
              pageSizeOptions={['10', '20', '30', '50', '100', '200', '500']}
              total={pagination ? pagination.total : 0}
              showTotal={(total) => `共 ${total} 条`}
              current={pagination ? pagination.current : 1}
              onShowSizeChange={(current: number, size: number) => {
                if (pagination) {
                  pagination.onChange(1, size)
                }
              }}
              onChange={(page: number, pageSize?: number) => {
                if (pagination) {
                  pagination.onChange(page, pageSize)
                }
              }}
            />
          </div>
        ) : null}
      </Spin>
    </div>
  )
}

export default CustomTableGrid
import { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import {
  Button,
  Checkbox,
  Pagination,
  Spin,
  Row,
  Col,
  Table,
  Tooltip,
  Popover,
  Dropdown,
} from 'antd'
import {
  CloseOutlined,
  MenuOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import Style from './table.module.scss'
import usehook, { Props, checkbox } from './hook'
import { Resizable } from 'react-resizable'
import FilterList from './FilterList'

// 列头可拖动
const ResizeableTitle = (props: any) => {
  const { onResize, width, title, ...restProps } = props
  // 添加偏移量
  const [offset, setOffset] = useState<number>(0)
  if (!title && !onResize) {
    return <th {...restProps} />
  }
  return (
    <Resizable
      width={width + offset}
      height={0}
      // 拖拽事件实时更新
      onResize={(e, { size }) => {
        // 这里只更新偏移量，数据列表其实并没有伸缩
        setOffset(size.width - width)
      }}
      // 拖拽结束更新
      onResizeStop={(...argu) => {
        // 拖拽结束以后偏移量归零
        setOffset(0)
        // 这里是props传进来的事件，在外部是列数据中的onHeaderCell方法提供的事件，请自行研究官方提供的案例
        onResize(...argu)
      }}
      handle={
        <span
          // 有偏移量显示竖线
          className={`${Style['react-resizable-handle']} ${
            offset ? Style['active'] : ''
          }`}
          // 拖拽层偏移
          style={{ transform: `translateX(${offset}px)` }}
          onClick={(e) => {
            // 取消冒泡，不取消貌似容易触发排序事件
            e.stopPropagation()
            e.preventDefault()
          }}
        />
      }
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  )
}

//  自定义 table组件
const CustomTableGrid = (props: Props) => {
  const {
    dataSource,
    listColumn,
    headerTool,
    title,
    loading,
    onChangeCheck,
    Y,
    X,
    scrollCustom,
    scrollStyle,
    pagination,
    getRowStyle,
    ScreenColKey,
    ScreenColOnlyKey,
    selectRowType,
    paginationBtn,
    selfAdaptScroll,
    showSize,
    rowKey,
    defaultlist,
    onSelectRow,
    onSelectAll,
    filterType,
    positionFixed,
    filterSlotRef,
    ...restProps
  } = props
  const {
    state,
    onHandleChange,
    onHandleChange2,
    onKeyDown,
    handleVisibleChange,
    onSelectionChanged,
    onCellFocused,
    onClickClose,
    onClickSave,
    handleResize,
  } = usehook(props)
  const {
    filterbtn,
    checkBoxData,
    checkLength,
    filterCol,
    Column,
    focusData,
    highlight,
    checkedRows,
  } = state

  // Portal：挂载后检测外部插槽是否就绪，就绪后将按钮 portal 过去
  const [portalReady, setPortalReady] = useState(false)
  useEffect(() => {
    if (filterSlotRef?.current) {
      setPortalReady(true)
    }
  }, [filterSlotRef])
  const usePortal = filterType && portalReady && filterSlotRef?.current

  // 渲染 checkbox
  const renderCheckBox = (item: checkbox, index: number) => {
    return (
      <Checkbox
        key={item.key}
        onChange={() => onHandleChange(item, index)}
        checked={item.check}
        disabled={item.disabled}
      >
        <MenuOutlined
          style={{
            color: '#ddd',
            marginRight: '8px',
            verticalAlign: 'baseline',
            fontSize: '10px',
          }}
        />
        {item.name}
      </Checkbox>
    )
  }

  // 获取必选列key数组
  const getFixedItems = () => {
    if (!ScreenColOnlyKey) return undefined
    return Array.isArray(ScreenColOnlyKey)
      ? ScreenColOnlyKey
      : [ScreenColOnlyKey]
  }

  // 渲染列设置按钮（filterType模式使用Dropdown + FilterList）
  const renderColumnSetting = () => {
    if (filterType) {
      return (
        <Dropdown
          trigger={['click']}
          onOpenChange={handleVisibleChange}
          open={filterbtn}
          getPopupContainer={() => document.body}
          dropdownRender={() => (
            <div className="checkboxbox">
              <div className="checkboxheader">
                <span>可用列</span>
                <Button
                  className="icon"
                  onClick={onClickClose}
                  icon={<CloseOutlined />}
                />
              </div>
              <FilterList
                data={checkBoxData}
                fixedItems={getFixedItems()}
                onSave={(param) => onClickSave(param)}
                onHandleChange={onHandleChange2}
                positionFixed={positionFixed}
              />
            </div>
          )}
        >
          <Button
            type="link"
            size="small"
            className={Style.filterBtn}
            icon={<UnorderedListOutlined />}
          ></Button>
        </Dropdown>
      )
    }

    if (ScreenColKey) {
      return (
        <Popover
          trigger="click"
          placement="bottomRight"
          title="可配置字段"
          onOpenChange={handleVisibleChange}
          content={
            <div className="checkboxbox">
              <Row>
                {checkBoxData.map((item, index) => (
                  <Col key={item.key} span={24} className="item">
                    {renderCheckBox(item, index)}
                  </Col>
                ))}
              </Row>
            </div>
          }
        >
          <Button
            type="link"
            size="small"
            className={Style.filterBtn}
            icon={<UnorderedListOutlined />}
          ></Button>
        </Popover>
      )
    }

    return null
  }

  // 渲染列：filterType模式按Column（已排序）渲染，否则按listColumn原始顺序
  const renderColumns = () => {
    if (filterType) {
      return Column.map((item: any, index: number) =>
        filterCol.indexOf(item.key) !== -1 ? (
          <Table.Column
            {...item}
            key={`${index}_A`}
            ellipsis
            align="left"
            width={Column[index]?.width ?? 100}
            dataIndex={item.key}
            title={
              item.titleRender ? (
                item.titleRender
              ) : (
                <span title={item.title}>{item.title}</span>
              )
            }
            render={item.render}
            onHeaderCell={() => {
              return {
                className: Style['headercell'],
                width: Column[index]?.width ?? 100,
                onResize: handleResize(index),
              }
            }}
            onCell={(record: any, rowIndex) => {
              return {
                onClick: () =>
                  onCellFocused(
                    item.key ? record[String(item.key)] : null,
                    index,
                    rowIndex ? rowIndex : 0,
                  ),
                className: `${Style['cell']} ${
                  focusData.x === index && focusData.y === rowIndex
                    ? Style['focus']
                    : ''
                } ${
                  highlight.x === index && highlight.y === rowIndex
                    ? Style['copyHighlight']
                    : ''
                }`,
              }
            }}
          />
        ) : null,
      )
    }

    // 原有渲染逻辑
    return listColumn.map((item, index) =>
      filterCol.indexOf(item.key) !== -1 ? (
        <Table.Column
          {...item}
          key={`${index}_A`}
          ellipsis
          align="left"
          width={Column?.[index]?.width ?? 100}
          dataIndex={item.key}
          title={
            item.titleRender ? (
              item.titleRender
            ) : (
              <span title={item.title}>{item.title}</span>
            )
          }
          render={item.render}
          onHeaderCell={() => {
            return {
              className: Style['headercell'],
              width: Column?.[index]?.width ?? 100,
              onResize: handleResize(index),
            }
          }}
          onCell={(record, rowIndex) => {
            return {
              onClick: () =>
                onCellFocused(
                  item.key ? record[String(item.key)] : null,
                  index,
                  rowIndex ? rowIndex : 0,
                ),
              className: `${Style['cell']} ${
                focusData.x === index && focusData.y === rowIndex
                  ? Style['focus']
                  : ''
              } ${
                highlight.x === index && highlight.y === rowIndex
                  ? Style['copyHighlight']
                  : ''
              }`,
            }
          }}
        />
      ) : null,
    )
  }

  // 是否需要显示表头行（有标题、headerTool、或非portal的列设置按钮时才显示）
  const showHeaderRow =
    headerTool || title || (!usePortal && (filterType || ScreenColKey))

  return (
    <div
      className={`${Style.tablebox} ${
        selfAdaptScroll && Style['table-self-adapt']
      }`}
    >
      {/* 将列设置按钮 portal 到外部插槽 */}
      {usePortal &&
        ReactDOM.createPortal(renderColumnSetting(), filterSlotRef!.current!)}
      {showHeaderRow && (
        <div className={Style.headertitle}>
          {/* 左侧box */}
          <div className={Style.headertoolleft}>{headerTool}</div>
          {/* 标题 */}
          <div className={Style.headertext}>{title}</div>
          {/* 右侧筛选条件 */}
          <div className={Style.headertoolright}>
            {!usePortal && renderColumnSetting()}
          </div>
        </div>
      )}
      <Spin tip="数据加载中..." spinning={loading}>
        <div id="components-table-resizable-column">
          <Table
            rowKey={rowKey || 'id'}
            bordered
            scroll={
              selfAdaptScroll
                ? { x: X ? X : 800, y: 'calc(100% - 56px)' }
                : scrollCustom
                  ? scrollCustom
                  : { x: X ? X : 800, y: `calc(100vh - ${Y ? Y + 56 : 256}px)` }
            }
            style={
              selfAdaptScroll
                ? {
                    height: '100%',
                    borderColor: '#cccccc',
                  }
                : scrollStyle
                  ? scrollStyle
                  : {
                      height: `calc(100vh - ${Y ? Y : 200}px)`,
                      borderColor: '#cccccc',
                    }
            }
            getPopupContainer={(triggerNode) => document.body ?? triggerNode}
            pagination={false}
            dataSource={dataSource}
            components={{
              header: {
                cell: ResizeableTitle,
              },
            }}
            onRow={(record) => ({
              style: getRowStyle ? getRowStyle(record) : undefined,
              onCopy: onKeyDown,
            })}
            rowSelection={
              onChangeCheck
                ? {
                    selectedRowKeys: checkLength,
                    onChange: onSelectionChanged,
                    type: selectRowType ? selectRowType : 'checkbox',
                    columnWidth: '30px',
                    getCheckboxProps: (record) => ({
                      disabled: record.isDisabled || record.hideCheckbox,
                      className: record.hideCheckbox ? 'hidden' : '',
                    }),
                    onSelect: onSelectRow,
                    onSelectAll,
                  }
                : undefined
            }
            {...restProps}
          >
            {renderColumns()}
          </Table>
        </div>
        {pagination ? (
          <div className={Style.pagination}>
            <div className={Style.paginationleft}>
              {onChangeCheck ? (
                checkLength.length ? (
                  `已选择${defaultlist?.length ?? checkLength.length}项内容`
                ) : (
                  <span style={{ color: '#AAA' }}>
                    共{pagination.total}项内容
                  </span>
                )
              ) : null}
              {paginationBtn && (
                <>
                  {!checkLength.length && (
                    <span className={Style.paginationBtnTitle}>
                      <i />
                      <span>批量操作</span>
                    </span>
                  )}
                  <div className={Style.paginationBtnBox}>
                    {paginationBtn.map((item) => (
                      <Tooltip
                        key={item.name}
                        placement="bottomLeft"
                        title={`需勾选表格选项后进行批量${item.name}`}
                        overlayClassName={`${Style.paginationBtnToolTip} ${
                          checkLength.length
                            ? Style.paginationBtnToolTipHidden
                            : ''
                        }`}
                      >
                        <Button
                          onClick={() => item.event(checkLength, checkedRows)}
                          disabled={!checkLength.length}
                          style={{ marginRight: '8px' }}
                        >
                          {item.name}
                        </Button>
                      </Tooltip>
                    ))}
                  </div>
                </>
              )}
            </div>
            <Pagination
              showSizeChanger={showSize ? true : false}
              showQuickJumper
              pageSize={pagination?.pagesize || 25}
              pageSizeOptions={['10', '20', '30', '50', '100', '200', '500']}
              total={pagination ? pagination.total : 0}
              showTotal={(total) => `共 ${total} 条`}
              current={pagination ? pagination.current : 1}
              onShowSizeChange={(current: number, size: number) => {
                if (pagination) {
                  pagination.onChange(1, size)
                }
              }}
              onChange={(page: number, pageSize?: number) => {
                if (pagination) {
                  pagination.onChange(page, pageSize)
                }
              }}
            />
          </div>
        ) : null}
      </Spin>
    </div>
  )
}

export default CustomTableGrid
